import { type ApiSchemas, call } from '@rental/contracts/client'
import { DAY_RATES_MAX_MIN_DAYS, DAY_RATES_MAX_PRICE_CENTS } from '@rental/domain'
import { format, formatDate, formatEuro, INTL_LOCALE, plural } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { TriangleAlert } from 'lucide-react'
import {
  type FormEvent,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { DataTable } from '../components/DataTable'
import { DateStepper } from '../components/DateStepper'
import { Dialog } from '../components/Dialog'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { ReasonList } from '../components/SubPanel'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import {
  type BulkForm,
  type BulkRequest,
  bulkRequest,
  type MinMode,
  type PriceMode,
  type StopMode,
  WEEKDAYS,
  WEEKEND,
  type Weekday,
} from './bulk'
import { valuesShort } from './cell'
import { bigMoves, movesByMoreThanHalf } from './guards'

type Preview = ApiSchemas['StaffPricesBulkPreview']
type Change = Preview['changes'][number]
export type AppliedBulk = ApiSchemas['StaffPricesBulkApplied']

/** A weekday's short name in the app's language: 1 January 2024 was a Monday. */
function weekdayName(locale: 'it' | 'en', day: Weekday): string {
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { timeZone: 'UTC', weekday: 'short' }).format(
    new Date(Date.UTC(2024, 0, day)),
  )
}

function freshForm(categoryIds: readonly string[], from: string, to: string): BulkForm {
  return {
    categoryIds,
    from,
    to,
    weekdays: WEEKDAYS,
    priceMode: 'leave',
    amount: '',
    minPickupMode: 'leave',
    minPickup: '',
    minThroughMode: 'leave',
    minThrough: '',
    stopMode: 'leave',
  }
}

/**
 * W2-S S1: many days at once. The categories (all at first), the days from
 * and to, the weekdays (presets: all, the weekend), and what to set: the
 * price left, set, raised or lowered; each minimum left, cleared or set; stop
 * sale left, on or off. "Anteprima" asks the server what would change, every
 * day old → new, what is already so and what is skipped; a price moved by
 * more than half is flagged, and Applica then asks once more. Applica sends
 * the preview's fingerprint with the Owner's code, emptied after any answer:
 * a day changed since the preview refuses it, and the preview is asked again.
 * Any change to the form drops the preview: what is applied is what was seen.
 */
export function BulkDialog({
  dialogRef,
  opened,
  categories,
  today,
  from,
  to,
  onApplied,
  onStale,
}: {
  dialogRef: RefObject<HTMLDialogElement | null>
  /** Counts the openings: each one opens the dialog afresh. */
  opened: number
  categories: readonly { id: string; name: string }[]
  today: string
  /** The first and last days it starts from: the days shown, from today on. */
  from: string
  to: string
  /** After the dialog has closed. */
  onApplied: (applied: AppliedBulk) => void
  /** The days changed since the preview: the grid reads them again. */
  onStale: () => void
}) {
  const { locale, messages: m } = useLocale()
  const words = m.prices
  const { takeOver } = useAccess()
  const reasonsId = useId()
  const applyReasonsId = useId()
  const fresh = () =>
    freshForm(
      categories.map((category) => category.id),
      from,
      to,
    )
  const [seen, setSeen] = useState(opened)
  const [form, setForm] = useState<BulkForm>(fresh)
  const [preview, setPreview] = useState<{ body: BulkRequest; data: Preview } | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [stale, setStale] = useState(false)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  if (opened !== seen) {
    setSeen(opened)
    setForm(fresh())
    setPreview(null)
    setConfirming(false)
    setStale(false)
    setCode('')
    setProblem(null)
  }
  useLayoutEffect(() => {
    if (opened > 0 && !dialogRef.current?.open) dialogRef.current?.showModal()
  }, [opened, dialogRef])

  // A preview that arrives takes the focus to its summary: the button pressed stays, its answer is below.
  const summary = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (preview) summary.current?.focus()
  }, [preview])

  const request = bulkRequest(form, today)
  const reasonWords = request.ok
    ? []
    : request.reasons.map((reason) =>
        reason === 'amount'
          ? format(words.why.amount, { max: formatEuro(DAY_RATES_MAX_PRICE_CENTS, locale) })
          : reason === 'min'
            ? format(words.why.min, { max: DAY_RATES_MAX_MIN_DAYS })
            : words.why[reason],
      )
  const flagged = useMemo(() => (preview ? bigMoves(preview.data.changes) : []), [preview])
  const names = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  )
  const nameOf = (id: string) => names.get(id) ?? id

  /** Any change to what is asked drops the preview: what is applied is what was seen. */
  const change = (next: Partial<BulkForm>) => {
    setForm((current) => ({ ...current, ...next }))
    setPreview(null)
    setConfirming(false)
    setStale(false)
  }

  async function askPreview() {
    if (busy || !request.ok) return
    setProblem(null)
    setStale(false)
    setConfirming(false)
    setBusy('preview')
    const body = request.body
    const result = await call(api.POST('/v1/staff/prices/bulk/preview', { body }))
    setBusy(null)
    if (result.ok) {
      setPreview({ body, data: result.data })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
  }

  async function apply() {
    if (busy || !preview || !OWNER_CODE.test(code)) return
    // A price moved by more than half: asked once more before it is sent.
    if (flagged.length > 0 && !confirming) {
      setConfirming(true)
      return
    }
    setProblem(null)
    setBusy('apply')
    const result = await call(
      api.POST('/v1/staff/prices/bulk', {
        body: { ...preview.body, fingerprint: preview.data.fingerprint, code },
      }),
    )
    setBusy(null)
    // A code is spent whether or not the change went through: a new one is typed either way.
    setCode('')
    setConfirming(false)
    if (result.ok) {
      dialogRef.current?.close()
      onApplied(result.data)
      return
    }
    if (takeOver(result.problem)) return
    if (result.problem.kind === 'api' && result.problem.code === 'changed_since_preview') {
      setPreview(null)
      setStale(true)
      onStale()
      return
    }
    setProblem(result.problem)
  }

  const minChoice = (
    name: string,
    label: string,
    mode: MinMode,
    value: string,
    onMode: (mode: MinMode) => void,
    onValue: (value: string) => void,
  ) => (
    <div className="flex flex-col gap-2">
      <ChoiceChips
        name={name}
        label={label}
        options={[
          { value: 'leave', label: words.leave },
          { value: 'clear', label: words.clear },
          { value: 'value', label: words.setTo },
        ]}
        value={mode}
        onChange={onMode}
      />
      {mode === 'value' ? (
        <Field className={FIELD} label={`${label}: ${words.minDays}`}>
          {(control) => (
            <StaffInput
              {...control}
              type="number"
              inputMode="numeric"
              min={1}
              max={DAY_RATES_MAX_MIN_DAYS}
              step={1}
              value={value}
              onChange={(event) => onValue(event.target.value)}
            />
          )}
        </Field>
      ) : null}
    </div>
  )

  const applyReasons = preview && !OWNER_CODE.test(code) ? [words.why.code] : []

  return (
    <Dialog dialogRef={dialogRef} title={words.bulk} busy={busy !== null} className="max-w-3xl">
      <form
        noValidate
        className="flex flex-col gap-6"
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          void (preview ? apply() : askPreview())
        }}
      >
        <ChoiceChips
          multiple
          name="prices-bulk-categories"
          label={words.categories}
          options={categories.map((category) => ({ value: category.id, label: category.name }))}
          value={form.categoryIds}
          onChange={(categoryIds) => change({ categoryIds })}
        />
        <div className="flex flex-col gap-4">
          <DateStepper
            label={words.bulkFrom}
            value={form.from}
            today={today}
            step={1}
            onChange={(date) => change({ from: date })}
            words={{ earlier: words.fromEarlier, later: words.fromLater, today: words.fromToday }}
          />
          <DateStepper
            label={words.bulkTo}
            value={form.to}
            today={today}
            step={1}
            onChange={(date) => change({ to: date })}
            words={{ earlier: words.toEarlier, later: words.toLater, today: words.toToday }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <ChoiceChips
            multiple
            name="prices-bulk-weekdays"
            label={words.weekdays}
            options={WEEKDAYS.map((day) => ({
              value: String(day),
              label: weekdayName(locale, day),
            }))}
            value={form.weekdays.map(String)}
            onChange={(days) =>
              change({ weekdays: WEEKDAYS.filter((day) => days.includes(String(day))) })
            }
          />
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => change({ weekdays: WEEKDAYS })}
            >
              {words.allWeek}
            </Button>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => change({ weekdays: WEEKEND })}
            >
              {words.weekend}
            </Button>
          </div>
        </div>

        <fieldset className="flex flex-col gap-4">
          <legend className="mb-2 text-lg">{words.whatToSet}</legend>
          <div className="flex flex-col gap-2">
            <ChoiceChips<PriceMode>
              name="prices-bulk-price"
              label={words.priceMode}
              options={[
                { value: 'leave', label: words.leave },
                { value: 'to', label: words.setTo },
                { value: 'plus', label: words.upBy },
                { value: 'minus', label: words.downBy },
              ]}
              value={form.priceMode}
              onChange={(priceMode) => change({ priceMode })}
            />
            {form.priceMode !== 'leave' ? (
              <Field className={FIELD} label={words.amount} hint={words.priceHint}>
                {(control) => (
                  <StaffInput
                    {...control}
                    inputMode="decimal"
                    autoComplete="off"
                    className="figures"
                    value={form.amount}
                    onChange={(event) => change({ amount: event.target.value })}
                  />
                )}
              </Field>
            ) : null}
          </div>
          <div className={FIELD_ROW}>
            {minChoice(
              'prices-bulk-min-pickup',
              words.minPickupLabel,
              form.minPickupMode,
              form.minPickup,
              (minPickupMode) => change({ minPickupMode }),
              (minPickup) => change({ minPickup }),
            )}
            {minChoice(
              'prices-bulk-min-through',
              words.minThroughLabel,
              form.minThroughMode,
              form.minThrough,
              (minThroughMode) => change({ minThroughMode }),
              (minThrough) => change({ minThrough }),
            )}
          </div>
          <ChoiceChips<StopMode>
            name="prices-bulk-stop"
            label={words.stopSaleMode}
            options={[
              { value: 'leave', label: words.leave },
              { value: 'on', label: words.stopOn },
              { value: 'off', label: words.stopOff },
            ]}
            value={form.stopMode}
            onChange={(stopMode) => change({ stopMode })}
          />
        </fieldset>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3">
            <Button
              variant={preview ? 'outline' : 'staff'}
              className="rounded-sm"
              busy={busy === 'preview'}
              disabled={!request.ok && busy === null}
              aria-describedby={request.ok ? undefined : reasonsId}
              onClick={() => void askPreview()}
            >
              {words.preview}
            </Button>
          </div>
          <ReasonList id={reasonsId} reasons={busy ? [] : reasonWords} />
        </div>

        {stale ? (
          <Notice tone="warning" live="assertive">
            <p>{words.stale}</p>
            <Button
              variant="outline"
              className="mt-2 rounded-sm"
              busy={busy === 'preview'}
              disabled={!request.ok && busy === null}
              onClick={() => void askPreview()}
            >
              {words.previewAgain}
            </Button>
          </Notice>
        ) : null}

        {preview ? (
          <div className="flex flex-col gap-4">
            <h3 className="text-lg">{words.preview}</h3>
            <p ref={summary} tabIndex={-1} className="flex flex-col focus:outline-none">
              <span>{plural(locale, words.changesCount, preview.data.changes.length)}</span>
              <span>{plural(locale, words.unchangedCount, preview.data.unchanged)}</span>
              {preview.data.skipped.length > 0 ? (
                <span>{plural(locale, words.skippedCount, preview.data.skipped.length)}</span>
              ) : null}
            </p>
            {flagged.length > 0 ? (
              <Notice tone="warning">
                <p>{plural(locale, words.bigMoveCount, flagged.length)}</p>
              </Notice>
            ) : null}
            {preview.data.changes.length === 0 ? (
              <p className="text-text-muted">{words.noChanges}</p>
            ) : (
              <DataTable<Change>
                label={words.changesTable}
                columns={[
                  { header: words.colCategory, cell: (row) => nameOf(row.categoryId) },
                  {
                    header: words.colDay,
                    cell: (row) => formatDate(locale, row.day, 'short'),
                    rowHeader: true,
                  },
                  {
                    header: words.colBefore,
                    cell: (row) => valuesShort(locale, words, row.before),
                  },
                  { header: words.colAfter, cell: (row) => valuesShort(locale, words, row.after) },
                  {
                    header: words.colNote,
                    cell: (row) =>
                      movesByMoreThanHalf(row.before.priceCents, row.after.priceCents) ? (
                        <span className="flex items-center gap-1 font-bold text-warning">
                          <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
                          {words.bigMove}
                        </span>
                      ) : null,
                  },
                ]}
                rows={preview.data.changes}
                rowKey={(row) => `${row.categoryId}:${row.day}`}
              />
            )}
            {preview.data.skipped.length > 0 ? (
              <DataTable
                label={words.skippedTable}
                columns={[
                  { header: words.colCategory, cell: (row) => nameOf(row.categoryId) },
                  {
                    header: words.colDay,
                    cell: (row) => formatDate(locale, row.day, 'short'),
                    rowHeader: true,
                  },
                  { header: words.colReason, cell: () => words.noPriceToMove },
                ]}
                rows={preview.data.skipped}
                rowKey={(row) => `${row.categoryId}:${row.day}`}
              />
            ) : null}
            {preview.data.changes.length > 0 ? (
              <>
                <OwnerCodeField
                  className={FIELD}
                  label={words.code}
                  value={code}
                  onChange={setCode}
                />
                {confirming ? (
                  <Notice tone="warning" live="assertive">
                    <p>{words.confirmBig}</p>
                  </Notice>
                ) : null}
                <div className="flex flex-wrap justify-end gap-3">
                  {confirming ? (
                    <Button
                      variant="outline"
                      className="rounded-sm"
                      disabled={busy !== null}
                      onClick={() => setConfirming(false)}
                    >
                      {words.backToPreview}
                    </Button>
                  ) : null}
                  <Button
                    type="submit"
                    variant="staff"
                    busy={busy === 'apply'}
                    disabled={applyReasons.length > 0 && busy === null}
                    aria-describedby={applyReasons.length > 0 ? applyReasonsId : undefined}
                  >
                    {confirming ? words.confirmApply : words.apply}
                  </Button>
                </div>
                <ReasonList id={applyReasonsId} reasons={busy ? [] : applyReasons} />
              </>
            ) : null}
          </div>
        ) : null}

        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap justify-end gap-3">
          <Button
            variant="outline"
            className="rounded-sm"
            disabled={busy !== null}
            onClick={() => dialogRef.current?.close()}
          >
            {words.close}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}
